import React, { useState, useEffect } from "react";
import ApiDevice from "../../../app/api/admin/device/ApiDevice";
import Modal from "../../components/modals/modal";
import ModalYesNo from "../../components/modals/modal-yes-no";
import Button from "../../components/button";
import Select from "../../components/inputs/select";
import Loader from "../../components/loader";

// Пар в одном запросе — не больше (перенос синхронный, бэкенд режет на валидации).
const MAX_PAIRS = 50;

// Аккаунт привязан — external_id положительное число (так же считает бэкенд; "0" и "" — нет).
function hasAccount(device) {
    return Number(device.external_id) > 0;
}

// Есть ли у источника что переносить — без аккаунта и прокси бэкенд пару отклонит.
function hasTransferData(device) {
    return hasAccount(device) || Boolean(device.proxy_id);
}

// Что есть у источника — для строки пары: аккаунт, прокси.
function describeSource(device) {
    const parts = [];
    if (hasAccount(device)) parts.push(`аккаунт #${device.external_id}`);
    if (device.proxy_id) parts.push(`прокси #${device.proxy_id}`);
    return parts.length > 0 ? parts.join(', ') : 'переносить нечего';
}

// Источник и его приёмники: устройство (show) и кандидаты (transfer-targets). null — не загрузилось;
// targets null — кандидатов загрузить не удалось (не путать с «свободных нет»).
async function loadSource(id) {
    const [device, targets] = await Promise.all([
        ApiDevice.Show(id).getResponse(),
        ApiDevice.TransferTargets(id).getResponse(),
    ]);
    if (!device) return null;
    // В карточке нода — объектом node, а не node_id / node_name, как в списке и у кандидатов.
    return {
        device: { ...device, node_id: device.node?.id ?? null, node_name: device.node?.name ?? null },
        targets,
    };
}

// Перенос данных устройства (POST devices/batch/transfer): аккаунт, прокси с режимом, exit-IP и
// часовой пояс переезжают с источника на приёмник, источник очищается и разлогинивается, на приёмнике
// ставится вход. Источники — отмеченные устройства; приёмники каждому отдаёт бэкенд (transfer-targets):
// пустые активные устройства, неподходящие — с причиной. Приёмник подставляется сразу — первый
// доступный на той же ноде, его можно сменить. «Проверить» — тот же запрос с dry_run: отказы по парам
// видно до переноса. «Перенести» — с подтверждением.
export default function DeviceTransferModal({ sourceIds, onClose, onDone }) {
    const [_sources, set_sources] = useState(null); // [{device, targets}] по порядку отметки; null — грузятся
    const [_targets, set_targets] = useState({});   // {from_id: to_id (строкой)}
    const [_action, set_action] = useState(null);   // 'check' | 'transfer' | null
    const [_results, set_results] = useState(null); // {from_id: {result, error?, moved?}} последнего запроса
    // Источники, с которых уже перенесено: их пары закрыты, остальные (отказы) можно поправить и
    // отправить снова, не открывая модалку заново.
    const [_doneIds, set_doneIds] = useState([]);
    const [_doneResults, set_doneResults] = useState({}); // {from_id: итог переноса} — для закрытых пар
    const [_error, set_error] = useState(null);
    const [_isOpenModalConfirm, set_isOpenModalConfirm] = useState(false);

    useEffect(() => {
        let isActual = true;
        (async () => {
            const loaded = await Promise.all(sourceIds.map(loadSource));
            if (!isActual) return;
            const sources = loaded.filter(Boolean);
            if (sources.length < loaded.length) set_error('Не все отмеченные устройства удалось загрузить');
            set_sources(sources);
            // Источники с данными в приёмники не годятся; отмеченные пустые — годятся.
            const busyIds = sources.filter(({ device }) => hasTransferData(device)).map(({ device }) => device.id);

            // Подставить приёмники: каждому источнику — первый доступный и ещё не занятый на той же
            // ноде. Источнику без данных — нет: он отнял бы пустое устройство у настоящего источника.
            const used = new Set();
            const targets = {};
            sources.forEach(({ device, targets: candidates }) => {
                if (!hasTransferData(device) || !candidates) return;
                const target = candidates.find((candidate) => candidate.available
                    && (candidate.node_id === device.node_id) && !used.has(candidate.id) && !busyIds.includes(candidate.id));
                if (target) {
                    used.add(target.id);
                    targets[device.id] = String(target.id);
                }
            });
            set_targets(targets);
        })();
        return () => { isActual = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sources = _sources ?? [];
    // Отмеченные с данными — источники, приёмниками быть не могут.
    const sourceWithDataIds = sources.filter(({ device }) => hasTransferData(device)).map(({ device }) => device.id);
    // Кандидаты в приёмники для источника — без других источников.
    const usableTargets = ({ targets }) => (targets ?? []).filter((candidate) => !sourceWithDataIds.includes(candidate.id));

    // Варианты приёмника: сначала та же нода, потом остальные (заголовками). Неподходящие — неактивны с
    // причиной бэкенда; выбранные в других парах — тоже: устройство может быть только в одной паре.
    const targetOptions = (sourceItem) => {
        const source = sourceItem.device;
        const takenIds = new Set(Object.entries(_targets)
            .filter(([fromId]) => Number(fromId) !== source.id)
            .map(([, toId]) => toId));
        const toOption = (candidate, nodeHint) => {
            const id = String(candidate.id);
            if (!candidate.available) return [id, { label: candidate.name, disabled: true, hint: `(${candidate.reason})` }];
            if (takenIds.has(id)) return [id, { label: candidate.name, disabled: true, hint: '(уже в другой паре)' }];
            return [id, nodeHint ? { label: candidate.name, hint: nodeHint } : candidate.name];
        };
        const usable = usableTargets(sourceItem);
        const sameNode = usable.filter((candidate) => candidate.node_id === source.node_id);
        const otherNodes = usable.filter((candidate) => candidate.node_id !== source.node_id);
        return [
            ...(sameNode.length > 0 ? [[`__group:same`, { label: `Нода ${source.node_name ?? '—'}`, header: true }]] : []),
            ...sameNode.map((candidate) => toOption(candidate)),
            ...(otherNodes.length > 0 ? [[`__group:other`, { label: 'Другие ноды', header: true }]] : []),
            ...otherNodes.map((candidate) => toOption(candidate, `(${candidate.node_name ?? '—'})`)),
        ];
    };

    const pairs = sources
        .filter(({ device }) => !_doneIds.includes(device.id) && _targets[device.id])
        .map(({ device }) => ({ from_id: device.id, to_id: Number(_targets[device.id]) }));
    const isTooManyPairs = pairs.length > MAX_PAIRS;

    const run = async (isDryRun) => {
        if ((_action !== null) || (pairs.length === 0) || isTooManyPairs) return;
        set_action(isDryRun ? 'check' : 'transfer');
        set_error(null);
        try {
            const api = ApiDevice.BatchTransfer(pairs, { dry_run: isDryRun });
            api.__showErrors = false; // ошибки — в самой модалке
            const data = await api.getResponse();
            if (data === null) {
                // 422 валидации — например, устройство в двух парах; прочее — код ответа.
                const messages = Object.values(api.errors ?? {}).flat();
                set_error(messages.length > 0
                    ? messages.join('; ')
                    : `Не удалось выполнить перенос${api.code > 0 ? ` (код ${api.code})` : ''}`);
                return;
            }
            const resultsByFromId = Object.fromEntries((data.pairs ?? []).map((pair) => [pair.from_id, pair]));
            set_results(resultsByFromId);
            if (!isDryRun) {
                const doneNow = (data.pairs ?? []).filter((pair) => pair.result);
                set_doneIds((previous) => [...previous, ...doneNow.map((pair) => pair.from_id)]);
                set_doneResults((previous) => ({
                    ...previous,
                    ...Object.fromEntries(doneNow.map((pair) => [pair.from_id, pair])),
                }));
                if ((doneNow.length > 0) && onDone) onDone();
            }
        } finally {
            set_action(null);
        }
    };

    const isBusy = _action !== null;
    // Всё перенесено — остаётся только «Готово». Источники без данных не в счёт: пары у них нет.
    const sourcesWithData = sources.filter(({ device }) => hasTransferData(device));
    const isAllDone = (sourcesWithData.length > 0) && sourcesWithData.every(({ device }) => _doneIds.includes(device.id));

    return <Modal isOpenModal={true} closeModal={isBusy ? () => {} : onClose} title="Перенос данных устройства"
        className="devices__transfer">
        <div className="devices__change-type-hint">
            Аккаунт, прокси (с режимом), exit-IP и часовой пояс переезжают на приёмник. Roblox на источнике
            останавливается (если устройство доступно) и разлогинивается, на приёмнике ставится вход — он выполнится, когда встанет
            прокси. Сначала «Проверить» — отказы по парам видно до переноса.
        </div>

        {_sources === null ? <Loader text="Устройства…"/> : <div className="devices__transfer-pairs">
            {sources.map((source) => {
                const { device } = source;
                const isDone = _doneIds.includes(device.id);
                const result = isDone ? _doneResults[device.id] : (_results?.[device.id] ?? null);
                const isTargetsFailed = !isDone && hasTransferData(device) && (source.targets === null);
                const isNoTargets = !isDone && hasTransferData(device) && !isTargetsFailed
                    && !usableTargets(source).some((candidate) => candidate.available);
                return <div key={device.id} className="devices__transfer-pair">
                    <div className="devices__transfer-source">
                        <span className="devices__transfer-name">{device.name}</span>
                        <span className="devices__transfer-meta">{device.node_name ?? '—'} · {describeSource(device)}</span>
                    </div>
                    <span className="devices__transfer-arrow">→</span>
                    <div className="devices__transfer-target">
                        <Select className="input_wf" options={targetOptions(source)} value={_targets[device.id] ?? ''}
                            isNull={true} hasLabel={false} readOnly={isBusy || isDone || !hasTransferData(device)}
                            onChange={(e) => {
                                set_targets((previous) => ({ ...previous, [device.id]: e.target.value }));
                                // Итог сбрасываем только у этой пары — отказы других остаются видны.
                                set_results((previous) => {
                                    if (!previous) return previous;
                                    const next = { ...previous };
                                    delete next[device.id];
                                    return next;
                                });
                                set_error(null);
                            }}/>
                    </div>
                    {result ? <div className={`devices__transfer-result devices__transfer-result_${result.result ? 'ok' : 'refused'}`}>
                        {result.result
                            ? (isDone ? `Перенесено: ${(result.moved ?? []).join(', ') || '—'}` : 'Можно переносить')
                            : result.error}
                    </div> : null}
                    {!result && isNoTargets ? <div className="devices__transfer-result devices__transfer-result_refused">
                        Переносить некуда: нет свободных активных устройств (без аккаунта, прокси и exit-IP)
                        {usableTargets(source).length > 0 ? ' — подходящие сейчас недоступны, причина — в списке' : ''}.
                    </div> : null}
                    {!result && isTargetsFailed ? <div className="devices__transfer-result devices__transfer-result_refused">
                        Не удалось загрузить список приёмников — закройте и откройте перенос снова.
                    </div> : null}
                </div>;
            })}
            {sources.length === 0 ? <div className="devices__change-type-hint">Отмеченных устройств нет.</div> : null}
        </div>}

        {isTooManyPairs ? <div className="devices__change-type-error">
            За раз — не больше {MAX_PAIRS} пар, сейчас {pairs.length}. Снимите выбор приёмника у лишних.
        </div> : null}
        {_error ? <div className="devices__change-type-error">{_error}</div> : null}

        <div className="devices__transfer-actions">
            {isAllDone
                ? <Button className="btn-success" onClick={onClose}>Готово</Button>
                : <>
                    <Button disabled={isBusy || (pairs.length === 0) || isTooManyPairs} onClick={() => run(true)}>
                        {_action === 'check' ? 'Проверка…' : 'Проверить'}
                    </Button>
                    <Button className="btn-warn" disabled={isBusy || (pairs.length === 0) || isTooManyPairs}
                        onClick={() => set_isOpenModalConfirm(true)}>
                        {_action === 'transfer' ? 'Перенос…' : `Перенести (${pairs.length})`}
                    </Button>
                </>}
        </div>

        <ModalYesNo
            isOpenModal={_isOpenModalConfirm}
            closeModal={() => set_isOpenModalConfirm(false)}
            actionBtnYes={() => { set_isOpenModalConfirm(false); run(false); }}
            title={`Перенести данные с ${pairs.length} устр.? Источники очистятся, Roblox на них разлогинится.`}
        />
    </Modal>;
}
